import React from 'react';
import {
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_BUTTON_DEFAULT, V4_DIALOG_CONTENT, V4_ICON_BUTTON, V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';

import { DeleteOutlined as DeleteOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Title, Loader, ReloadIconButton } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize, INT_MAX } from '../../constants/constants';
import { notifications, extractApiErrorMessage } from '../../utils';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ApiService } from '../../hooks';
import {
  HealthSystemDomainEmissionSourceUsageEntriesViewModelsEmissionSourceUsageEntryViewModel,
  HealthSystemDomainEmissionSourcesViewModelsEmissionSourceViewModel,
  HealthSystemDomainEmissionSourceUsageEntriesCommandHandlersAddEmissionSourceUsageEntryCommand,
  HealthSystemDomainEmissionSourceUsageEntriesCommandHandlersUpdateEmissionSourceUsageEntryCommand
} from '../../api/HealthSystemApi';

const EmissionSourceUsage: React.FC = () => {
  const { t } = useTranslation();
  const styles = sxStyles(useTheme());
  const apiService = new ApiService();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [usages, setUsages] = React.useState<
    HealthSystemDomainEmissionSourceUsageEntriesViewModelsEmissionSourceUsageEntryViewModel[]
  >([]);
  const [emissionSources, setEmissionSources] = React.useState<
    HealthSystemDomainEmissionSourcesViewModelsEmissionSourceViewModel[]
  >([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [selectedYear, setSelectedYear] = React.useState<number>(new Date().getFullYear());
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editId, setEditId] = React.useState<string | null>(null);

  const [createForm, setCreateForm] =
    React.useState<HealthSystemDomainEmissionSourceUsageEntriesCommandHandlersAddEmissionSourceUsageEntryCommand>({
      emissionSourceId: '',
      date: '',
      amount: 0,
      notes: ''
    });

  const [editForm, setEditForm] =
    React.useState<HealthSystemDomainEmissionSourceUsageEntriesCommandHandlersUpdateEmissionSourceUsageEntryCommand>({
      id: '',
      amount: 0,
      notes: ''
    });

  const loadUsages = async (year: number) => {
    setIsBusy(true);
    const currentPage = page === 0 ? 1 : page + 1;

    apiService.api.emissionSourceSettings
      .emissionSourceSettingsGet({ Year: year, Page: currentPage, Size: rowsPerPage })
      .then((result) => {
        setUsages(result.data.data);
        setTotal(result.data.total);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  const loadEmissionSources = async () => {
    apiService.api.emissionSourceSettings.emissionSourceSettingsGetAll({ Page: 1, Size: INT_MAX }).then((result) => {
      setEmissionSources(result.data.data);
    });
  };

  const handleOpenCreateDialog = () => {
    setCreateForm({ emissionSourceId: '', date: '', amount: 0, notes: '' });
    setCreateDialogOpen(true);
  };

  const handleOpenEditDialog = (
    row: HealthSystemDomainEmissionSourceUsageEntriesCommandHandlersUpdateEmissionSourceUsageEntryCommand
  ) => {
    setEditForm({ id: row.id, amount: row.amount, notes: row.notes || '' });
    setEditId(row.id);
    setEditDialogOpen(true);
  };

  React.useEffect(() => {
    if (!loaded) loadUsages(selectedYear);
    loadEmissionSources();
  }, [loaded]);

  const handleSubmitCreate = async () => {
    setIsBusy(true);
    apiService.api.emissionSourceSettings
      .emissionSourceSettingsAddEmissionSourceUsage(createForm)
      .then(() => {
        notifications.success(t('f-emission-entry-create-success'));
        setCreateDialogOpen(false);
        setLoaded(false);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsBusy(false));
  };

  const handleSubmitEdit = async () => {
    if (!editId) return;
    setIsBusy(true);
    apiService.api.emissionSourceSettings
      .emissionSourceSettingsUpdateEmissionSourceUsage(editForm)
      .then(() => {
        notifications.success(t('f-emission-entry-update-success'));
        setEditDialogOpen(false);
        setLoaded(false);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsBusy(false));
  };

  const handleDeleteUsage = (id: string) => {
    if (!window.confirm(t('f-emission-entry-delete-confirm'))) return;
    setIsBusy(true);

    apiService.api.emissionSourceSettings
      .emissionSourceSettingsDeleteEmissionSourceUsageEntry({ id })
      .then(() => {
        notifications.success(t('f-emission-entry-delete-success'));
        setLoaded(false);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsBusy(false));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };

  const handleYearChange = (value: unknown) => {
    const date = value as Date | null;
    if (date) {
      const year = date.getFullYear();
      setSelectedYear(year);
      loadUsages(year);
    }
  };

  return (
    <>
      <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container sx={styles.paperHeader}>
              <Grid item container md={6} xs={6} spacing={1}>
                <Grid item>
                  <Icon color="primary">science</Icon>
                </Grid>
                <Grid item>
                  <Title>{t('f-emission-usage-title')}</Title>
                </Grid>
                <Grid item>
                  <Chip label={total} variant="outlined" />
                </Grid>
                <Grid item>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      views={['year']}
                      label="Select Year"
                      value={new Date(selectedYear, 0)}
                      onChange={handleYearChange}
                      minDate={new Date(2020, 0)}
                      maxDate={new Date()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small'
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Grid item container md={6} xs={6} sx={styles.justifyRight}>
                <Grid item>
                  <ReloadIconButton action={() => setLoaded(false)} />
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenCreateDialog}
                    startIcon={<Icon>add_circle_outline</Icon>}
                  >
                    {t('f-com-create-new')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

            {loaded && (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead sx={styles.tableHeader}>
                    <TableRow>
                      <TableCell align="center">{t('f-emission-name')}</TableCell>
                      <TableCell align="center">{t('f-emission-unit')}</TableCell>
                      <TableCell align="center">{t('f-emission-date')}</TableCell>
                      <TableCell align="center">{t('f-emission-amount')}</TableCell>
                      <TableCell align="center">{t('f-emission-co2-value')}</TableCell>
                      <TableCell align="center">{t('f-emission-notes')}</TableCell>
                      <TableCell align="center">{t('f-com-actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usages.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align="center">{row.emissionSourceName}</TableCell>
                        <TableCell align="center">{row.unit}</TableCell>
                        <TableCell align="center">{row.date}</TableCell>
                        <TableCell align="center">{row.amount}</TableCell>
                        <TableCell align="center">{row.co2Value}</TableCell>
                        <TableCell align="center">{row.notes}</TableCell>
                        <TableCell align="center">
                          <Tooltip arrow title={t('f-com-edit')}>
                            <IconButton
                              id={`upd-${row.id}`}
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEditDialog(row)}
                              sx={V4_ICON_BUTTON_SMALL}
                            >
                              <Icon>{'edit'}</Icon>
                            </IconButton>
                          </Tooltip>
                          <IconButton onClick={() => handleDeleteUsage(row.id)} sx={V4_ICON_BUTTON}>
                            <DeleteOutline color="error" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </Container>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>{editId ? t('f-emission-edit') : t('f-emission-create')}</DialogTitle>
        <DialogContent sx={V4_DIALOG_CONTENT}>
          <TextField
            fullWidth
            select
            label={t('f-emission-name')}
            value={createForm.emissionSourceId}
            onChange={(e) => setCreateForm({ ...createForm, emissionSourceId: e.target.value })}
            margin="dense"
            variant="standard"
          >
            {emissionSources.map((source) => (
              <MenuItem key={source.id} value={source.id}>
                {source.name} ({source.unitOfMeasure})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label={t('f-emission-date')}
            type="date"
            value={createForm.date}
            onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })}
            margin="dense"
            slotProps={{ inputLabel: { shrink: true } }}
            variant="standard"
          />
          <TextField
            fullWidth
            label={t('f-emission-amount')}
            type="number"
            value={createForm.amount}
            slotProps={{ htmlInput: { min: 0 } }}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (!isNaN(value) && value >= 0) {
                setCreateForm({ ...createForm, amount: parseFloat(e.target.value) });
              }
            }}
            margin="dense"
            variant="standard"
          />
          <TextField
            fullWidth
            label={t('f-emission-notes')}
            value={createForm.notes}
            onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
            margin="dense"
            variant="standard"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} color="inherit" sx={V4_BUTTON_DEFAULT}>
            {t('f-com-cancel')}
          </Button>
          <Button onClick={handleSubmitCreate} color="primary" variant="contained">
            {t('f-com-save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>{t('f-emission-edit')}</DialogTitle>
        <DialogContent sx={V4_DIALOG_CONTENT}>
          <TextField
            fullWidth
            label={t('f-emission-amount')}
            type="number"
            value={editForm.amount}
            onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) })}
            margin="dense"
            variant="standard"
          />
          <TextField
            fullWidth
            label={t('f-emission-notes')}
            value={editForm.notes}
            onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
            margin="dense"
            variant="standard"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit" sx={V4_BUTTON_DEFAULT}>
            {t('f-com-cancel')}
          </Button>
          <Button onClick={handleSubmitEdit} color="primary" variant="contained">
            {t('f-com-save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmissionSourceUsage;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    tableHeader: {
      background: colors.defaultGrey
    },
    paperHeader: {
      justifyContent: 'space-between'
    },
    justifyRight: {
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    withMarginLeft: {
      marginLeft: theme.spacing(1)
    },
    tableInfoChip: {
      marginLeft: theme.spacing(2),
      background: colors.defaultGrey
    }
  }) as const;
